import api from './api-config';

/**
 * Get untagged transactions with auto-suggested tags
 * @param {Object} filters - Filter options
 * @param {string[]} filters.transaction_types - Filter by transaction types
 * @param {number} filters.amount_min - Minimum amount filter
 * @param {number} filters.amount_max - Maximum amount filter
 * @param {number} filters.limit - Number of transactions to return (default: 50)
 * @param {number} filters.offset - Offset for pagination (default: 0)
 * @returns {Promise<Object>} Object containing transactions with suggestions and stats
 */
export const getUntaggedTransactionsWithSuggestions = async (filters = {}) => {
    try {
        const response = await api.post('/auto-tagging/suggestions', {
            filters: {
                limit: 50,
                offset: 0,
                ...filters
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching untagged transactions with suggestions:', error);
        throw error;
    }
};

/**
 * Apply a suggested tag to a transaction
 * @param {string} transactionId - The transaction ID
 * @param {number} tagId - The tag ID to apply
 * @param {number} [confidence] - Optional confidence score for tracking
 * @returns {Promise<Object>} Success response
 */
export const applyTagToTransaction = async (transactionId, tagId, confidence = null) => {
    try {
        const response = await api.post(`/auto-tagging/apply`, {
            transaction_id: transactionId,
            tag_id: tagId,
            confidence: confidence,
            source: 'auto_suggestion_approved'
        });
        return response.data;
    } catch (error) {
        console.error('Error applying tag to transaction:', error);
        throw error;
    }
};

/**
 * Apply multiple tags to multiple transactions in bulk
 * @param {Array} tagApplications - Array of {transaction_id, tag_id, confidence?} objects
 * @returns {Promise<Object>} Bulk operation result
 */
export const bulkApplyTags = async (tagApplications) => {
    try {
        const response = await api.post('/auto-tagging/bulk-apply', {
            applications: tagApplications.map(app => ({
                transaction_id: app.transaction_id,
                tag_id: app.tag_id,
                confidence: app.confidence || null,
                source: 'auto_suggestion_bulk_approved'
            }))
        });
        return response.data;
    } catch (error) {
        console.error('Error applying bulk tags:', error);
        throw error;
    }
};

/**
 * Skip/dismiss a transaction from auto-tagging suggestions
 * @param {string} transactionId - The transaction ID to skip
 * @param {string} [reason] - Optional reason for skipping
 * @returns {Promise<Object>} Success response
 */
export const skipTransaction = async (transactionId, reason = 'user_skipped') => {
    try {
        const response = await api.post('/auto-tagging/skip', {
            transaction_id: transactionId,
            reason: reason
        });
        return response.data;
    } catch (error) {
        console.error('Error skipping transaction:', error);
        throw error;
    }
};

/**
 * Get auto-tagging statistics for the current organization
 * @returns {Promise<Object>} Auto-tagging statistics
 */
export const getAutoTaggingStats = async () => {
    try {
        const response = await api.get('/auto-tagging/stats');
        return response.data;
    } catch (error) {
        console.error('Error fetching auto-tagging stats:', error);
        throw error;
    }
};

/**
 * Get auto-tagging configuration/rules for the organization
 * @returns {Promise<Object>} Auto-tagging configuration
 */
export const getAutoTaggingConfig = async () => {
    try {
        const response = await api.get('/auto-tagging/config');
        return response.data;
    } catch (error) {
        console.error('Error fetching auto-tagging config:', error);
        throw error;
    }
};

/**
 * Update auto-tagging configuration/rules
 * @param {Object} config - Configuration object
 * @param {number} config.min_confidence_threshold - Minimum confidence to show suggestions
 * @param {number} config.max_suggestions_per_transaction - Maximum suggestions per transaction
 * @param {boolean} config.enable_merchant_matching - Enable merchant-based matching
 * @param {boolean} config.enable_amount_matching - Enable amount-based matching
 * @param {boolean} config.enable_description_matching - Enable description-based matching
 * @returns {Promise<Object>} Updated configuration
 */
export const updateAutoTaggingConfig = async (config) => {
    try {
        const response = await api.put('/auto-tagging/config', config);
        return response.data;
    } catch (error) {
        console.error('Error updating auto-tagging config:', error);
        throw error;
    }
};

/**
 * Force regenerate suggestions for specific transactions
 * @param {string[]} transactionIds - Array of transaction IDs
 * @returns {Promise<Object>} Regeneration result
 */
export const regenerateSuggestions = async (transactionIds) => {
    try {
        const response = await api.post('/auto-tagging/regenerate', {
            transaction_ids: transactionIds
        });
        return response.data;
    } catch (error) {
        console.error('Error regenerating suggestions:', error);
        throw error;
    }
};

/**
 * Get feedback history for auto-tagging suggestions
 * @param {Object} filters - Filter options
 * @param {string} filters.start_date - Start date for filtering
 * @param {string} filters.end_date - End date for filtering
 * @param {number} filters.limit - Number of records to return
 * @returns {Promise<Object>} Feedback history
 */
export const getAutoTaggingFeedback = async (filters = {}) => {
    try {
        const response = await api.get('/auto-tagging/feedback', {
            params: filters
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching auto-tagging feedback:', error);
        throw error;
    }
};

/**
 * Provide feedback on a tag suggestion (for learning purposes)
 * @param {string} transactionId - The transaction ID
 * @param {number} tagId - The suggested tag ID
 * @param {string} feedback - 'positive', 'negative', or 'neutral'
 * @param {string} [comment] - Optional feedback comment
 * @returns {Promise<Object>} Feedback submission result
 */
export const provideSuggestionFeedback = async (transactionId, tagId, feedback, comment = null) => {
    try {
        const response = await api.post('/auto-tagging/feedback', {
            transaction_id: transactionId,
            tag_id: tagId,
            feedback: feedback,
            comment: comment
        });
        return response.data;
    } catch (error) {
        console.error('Error providing suggestion feedback:', error);
        throw error;
    }
};

/**
 * Run AI-powered categorization using Claude.
 * Analyzes untagged transactions and returns tag suggestions + proposed rules.
 */
export const runAiCategorization = async () => {
    try {
        const response = await api.post('/auto-tagging/ai-categorize');
        return response.data;
    } catch (error) {
        console.error('Error running AI categorization:', error);
        throw error;
    }
};